import { css } from '@emotion/css';
import { Theme } from '@mui/material';
import { curveMonotoneX } from '@visx/curve';
import { Area, Circle, LinePath } from '@visx/shape';
import { ScaleLinear } from 'd3-scale';

import { useStyles } from '@/hooks/useStyles';
import { AGPStats } from '@/models/AGPStatsModel.ts';

export type AGPCurvesProps = {
  agp: AGPStats;
  scales: {
    rescaleX: ScaleLinear<number, number>;
    rescaleY: ScaleLinear<number, number>;
  };
};

export const AGPCurves = ({ agp, scales }: AGPCurvesProps) => {
  const { rescaleX, rescaleY } = scales;
  const styles = useStyles(makeStyles);

  if (Object.keys(agp).length === 1) {
    const key = Object.keys(agp)[0];
    const values = Object.values(agp)[0];
    return (
      <Circle
        cx={rescaleX(key)}
        r={5}
        cy={rescaleY(values['50'])}
        className={styles.circle}
      />
    );
  }
  return (
    <>
      <LinePath
        strokeLinejoin="round"
        strokeLinecap="round"
        curve={curveMonotoneX}
        data={Object.entries(agp)}
        x={([hour]) => rescaleX(hour)}
        y={([, values]) => rescaleY(values['50'])}
        className={styles.median}
      />

      <Area
        curve={curveMonotoneX}
        data={Object.entries(agp)}
        x={([hour]) => rescaleX(hour)}
        y0={([, values]) => rescaleY(values['5'])}
        y1={([, values]) => rescaleY(values['95'])}
        className={styles.p90}
      />

      <Area
        curve={curveMonotoneX}
        data={Object.entries(agp)}
        x={([hour]) => rescaleX(hour)}
        y0={([, values]) => rescaleY(values['25'])}
        y1={([, values]) => rescaleY(values['75'])}
        className={styles.p50}
      />
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  circle: css`
    color: ${theme.palette.agp.median};
    fill: currentColor;
  `,
  p90: css`
    color: ${theme.palette.agp.p90};
    fill: currentColor;
  `,
  p50: css`
    color: ${theme.palette.agp.p50};
    fill: currentColor;
  `,
  median: css`
    color: ${theme.palette.agp.median};
    stroke: currentColor;
    stroke-width: 2;
  `,
});
